/**
 * GroomingNotesTab — Becky-T2.2 (issue #34).
 *
 * Per-pet bilingual notes feed. Authors a note in their working
 * language; the translator service mirrors it into the other language.
 * The UI surfaces both the original and translation side-by-side so
 * Jorge (Spanish-only) and Becky (English-only) can both read the
 * same context.
 *
 * UX guarantees:
 *   - Submitting an empty note is rejected client-side.
 *   - The note appears immediately as `translation_status='pending'`
 *     with a spinner-style label; UI polls a single refresh after 2s
 *     to pick up the translated row.
 *   - When translation fails, the row shows "Translation pending"
 *     plus a "Retry" button (AC3).
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Alert, Button } from '@ferro/ui';
import {
  createGroomingNote,
  fetchGroomingNotes,
  retranslateGroomingNote,
  type GroomingNoteEntry,
  type GroomingNoteLanguage,
} from '../../lib/api.js';

const LANGUAGE_LABELS: Record<GroomingNoteLanguage, string> = {
  en: 'English',
  es: 'Español',
};

const STATUS_LABELS: Record<GroomingNoteEntry['translation_status'], string> = {
  pending: 'Translating…',
  translated: 'Translation ready',
  failed: 'Translation pending',
};

function otherLanguage(lang: GroomingNoteLanguage): GroomingNoteLanguage {
  return lang === 'en' ? 'es' : 'en';
}

export interface GroomingNotesTabProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly dogId: string;
  readonly dogName: string;
  // The staffer's working language — defaults to 'en' to match the
  // English-default i18n scaffold. Becky toggles via the segmented
  // language picker.
  readonly defaultLanguage?: GroomingNoteLanguage;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; entries: GroomingNoteEntry[] };

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function GroomingNotesTab({
  apiUrl,
  token,
  dogId,
  dogName,
  defaultLanguage = 'en',
}: GroomingNotesTabProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [language, setLanguage] = useState<GroomingNoteLanguage>(defaultLanguage);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const res = await fetchGroomingNotes(apiUrl, token, dogId);
      setState({ kind: 'ready', entries: res.notes });
    } catch (err) {
      setState({
        kind: 'error',
        message:
          err instanceof Error ? err.message : 'Unable to load grooming notes',
      });
    }
  }, [apiUrl, token, dogId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;

    const trimmed = text.trim();
    if (trimmed.length === 0) {
      setError('Add a note before saving.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const res = await createGroomingNote(apiUrl, token, dogId, {
        original_language: language,
        original_text: trimmed,
      });
      setState((current) => {
        if (current.kind !== 'ready') return { kind: 'ready', entries: [res.note] };
        return { ...current, entries: [res.note, ...current.entries] };
      });
      setText('');

      // Translation runs server-side without blocking. Poll once after a
      // short delay to pick up the translated row — keeps the UI fresh
      // without a long-poll or websocket.
      window.setTimeout(() => {
        void load();
      }, 2_500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async (id: string): Promise<void> => {
    setRetryingId(id);
    try {
      const res = await retranslateGroomingNote(apiUrl, token, id);
      setState((current) => {
        if (current.kind !== 'ready') return current;
        return {
          ...current,
          entries: current.entries.map((n) => (n.id === id ? res.note : n)),
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <article
      className="ferro-dashboard__card"
      aria-label="Grooming notes"
      data-testid="grooming-notes"
    >
      <header className="ferro-dashboard__cardHeader">
        <h2 className="ferro-dashboard__cardTitle">
          Grooming notes for {dogName}
        </h2>
        {state.kind === 'ready' ? (
          <span className="ferro-dashboard__metric">{state.entries.length}</span>
        ) : null}
      </header>

      <form className="ferro-form" onSubmit={handleSubmit} noValidate>
        <div
          className="ferro-segmented"
          role="tablist"
          aria-label="Note language"
        >
          {(['en', 'es'] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              role="tab"
              aria-selected={language === lang}
              className={`ferro-segmented__btn${
                language === lang ? ' ferro-segmented__btn--active' : ''
              }`}
              onClick={() => setLanguage(lang)}
              data-testid={`grooming-lang-${lang}`}
            >
              {LANGUAGE_LABELS[lang]}
            </button>
          ))}
        </div>

        <label className="ferro-field">
          <span className="ferro-field__label">
            Note in {LANGUAGE_LABELS[language]}
          </span>
          <textarea
            className="ferro-field__input ferro-field__input--textarea"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              language === 'es'
                ? 'Pelaje matado en orejas, sensible en cadera derecha…'
                : 'Matted ears, sensitive on right hip…'
            }
            data-testid="grooming-text"
          />
        </label>

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="ferro-form__actions">
          <Button
            variant="primary"
            type="submit"
            disabled={submitting}
            data-testid="grooming-submit"
          >
            {submitting
              ? 'Saving…'
              : language === 'es'
                ? 'Guardar nota'
                : 'Save note'}
          </Button>
        </div>
      </form>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading grooming notes…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        state.entries.length === 0 ? (
          <p className="ferro-muted">No grooming notes yet.</p>
        ) : (
          <ul
            className="ferro-timeline"
            aria-label="Grooming notes timeline"
            data-testid="grooming-timeline"
          >
            {state.entries.map((note) => (
              <li
                key={note.id}
                className="ferro-timeline__item"
                data-testid={`grooming-entry-${note.id}`}
              >
                <span className="ferro-timeline__when">
                  {formatWhen(note.created_at)}
                  {note.staff_name ? ` · ${note.staff_name}` : ''} ·{' '}
                  {STATUS_LABELS[note.translation_status]}
                </span>
                <div className="ferro-grooming-note__pair">
                  <div className="ferro-grooming-note__pane">
                    <span className="ferro-field__label">
                      {LANGUAGE_LABELS[note.original_language]} (original)
                    </span>
                    <p className="ferro-timeline__what">{note.original_text}</p>
                  </div>
                  <div className="ferro-grooming-note__pane">
                    <span className="ferro-field__label">
                      {LANGUAGE_LABELS[otherLanguage(note.original_language)]} (
                      {note.translation_status === 'translated'
                        ? 'translation'
                        : 'translation pending'}
                      )
                    </span>
                    {note.translation_status === 'translated' &&
                    note.translated_text ? (
                      <p className="ferro-timeline__what">{note.translated_text}</p>
                    ) : note.translation_status === 'failed' ? (
                      <div>
                        <p className="ferro-muted">
                          {note.translation_error ?? 'Translation pending.'}
                        </p>
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--ghost"
                          onClick={() => void handleRetry(note.id)}
                          disabled={retryingId === note.id}
                          data-testid={`grooming-retry-${note.id}`}
                        >
                          {retryingId === note.id
                            ? 'Retrying…'
                            : 'Retry translation'}
                        </button>
                      </div>
                    ) : (
                      <p className="ferro-muted">Translating…</p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </article>
  );
}
